import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  Link,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { trainingAdminRequests } from 'redux/requests/training-admin.routes';
import { useApiCall } from 'redux/utils/api-hook';
import { Button, FlexContainer, IconLibrary } from '@act/storybook';
import { useAppNavigation } from 'react-components/app/routing/routes';
import { UserDto } from '@act/shared/data-transfer-objects';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const ResetTrainingCharacters = () => {
  const navigate = useAppNavigation();

  const [
    resetTrainingUsers,
    isResetting,
    makeResetApiCall,
    resetErrorResponse,
  ] = useApiCall(trainingAdminRequests.resetTrainingCharacters);

  const [initialTrainingUsers, isGettingUsers, makeTrainingUserApiCall] =
    useApiCall(trainingAdminRequests.getTrainingCharacters);

  useEffect(() => {
    void makeTrainingUserApiCall({ params: null, body: null });
  }, []);

  const [viewUsers, setViewUsers] = useState(false);

  const goToUser = (user: UserDto) => {
    navigate.toMemberPage(user.assignedGuideId, user.id);
  };

  const trainingUsers = useMemo(() => {
    return resetTrainingUsers ? resetTrainingUsers : initialTrainingUsers;
  }, [resetTrainingUsers, initialTrainingUsers]);

  const userColumns = useMemo(() => {
    if (!trainingUsers) return [];
    const columns: UserDto[][] = [];
    const usersPerColumn = 5;

    for (let i = 0; i < trainingUsers.length; i += usersPerColumn) {
      columns.push(trainingUsers.slice(i, i + usersPerColumn));
    }

    return columns;
  }, [trainingUsers]);

  const postReset = async () => {
    void makeResetApiCall({ params: null });
  };

  const showSuccess = resetTrainingUsers && !isResetting && !resetErrorResponse;
  const showError = resetErrorResponse && !isResetting;

  return (
    <FlexContainer direction="column" gap={1} height="fit-content">
      <Typography variant="titleLarge">
        Resetting Training Characters
      </Typography>
      <FlexContainer direction="column" gap={2} height="fit-content">
        <FlexContainer
          direction="row"
          gap={1}
          alignItems="center"
          height="fit-content"
        >
          <Button
            variant="contained"
            debounced
            onClick={postReset}
            disabled={isResetting}
          >
            Reset Training
          </Button>
          {showSuccess && (
            <IconLibrary iconName="CheckCircle" color="success" />
          )}
          {showError && <IconLibrary iconName="WarningCircle" color="error" />}
        </FlexContainer>
        {isResetting && <CircularProgress />}

        <Box>
          <Typography variant="bodyLarge" component="p">
            Training Users {trainingUsers && `(${trainingUsers.length})`}
            <IconButton onClick={() => setViewUsers(!viewUsers)}>
              {viewUsers ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Typography>

          <Collapse in={viewUsers} timeout="auto" unmountOnExit>
            {isGettingUsers && <CircularProgress />}
            {!isGettingUsers && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${userColumns.length}, 175px)`,
                  gap: 3,
                  marginTop: 2,
                }}
              >
                {userColumns.map((column, columnIndex) => (
                  <Box key={`column-${columnIndex}`}>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      {column.map(user => (
                        <li
                          key={user.id}
                          style={{ marginBottom: '8px', cursor: 'pointer' }}
                        >
                          <Link
                            onClick={() => goToUser(user)}
                            sx={{ cursor: 'pointer' }}
                          >
                            {user.firstName} {user.lastName}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </Box>
                ))}
              </Box>
            )}
          </Collapse>
        </Box>
      </FlexContainer>
    </FlexContainer>
  );
};

export default ResetTrainingCharacters;
